import {flatten, isEmpty} from 'lodash';

import {EditorBaseErrorsEnum, EditorValidationErrorsEnum} from '../../editors/commons/editorErrors';
import {Expression} from '../../editors/expressions/expressionModel';
import {ExpressionsEditorBaseErrorsEnum} from '../../editors/expressions/expressionsEditorErrors';
import {TransformerIdsEnum} from '../../editors/expressions/infer/transformers';
import {
  RULE_DATA_TABLE_MAX_COLUMN_COUNT,
  RULE_DATA_TABLE_MAX_ROW_COUNT,
  RuleDataTableErrors,
} from '../../editors/ruleEditor/dataTableErrors';
import {
  RuleEditorActionBaseErrorsEnum,
  RuleEditorConditionBaseErrorsEnum,
  RuleEditorTriggerBaseErrorsEnum,
} from '../../editors/ruleEditor/ruleEditorErrors';
import {checkActionValidity} from '../ruleActionHelpers';
import {checkANDConditionsValidity} from '../ruleConditionHelpers';
import {RuleIntegration} from '../ruleIntegrationModel';
import {RuleEditorState} from './state/ruleEditorState';

/*
 * Types.
 */

export const RuleEditorActionErrorsEnum = {
  ...EditorValidationErrorsEnum,
  ...RuleEditorActionBaseErrorsEnum,
  ...ExpressionsEditorBaseErrorsEnum,
};

export type RuleEditorActionErrorsEnum =
  | EditorValidationErrorsEnum
  | RuleEditorActionBaseErrorsEnum
  | ExpressionsEditorBaseErrorsEnum;

export const RuleEditorConditionErrorsEnum = {
  ...EditorValidationErrorsEnum,
  ...RuleEditorConditionBaseErrorsEnum,
  ...ExpressionsEditorBaseErrorsEnum,
};

export type RuleEditorConditionErrorsEnum =
  | EditorValidationErrorsEnum
  | RuleEditorConditionBaseErrorsEnum
  | ExpressionsEditorBaseErrorsEnum;

export const RuleEditorErrorsEnum = {
  ...EditorBaseErrorsEnum,
  ...RuleEditorActionErrorsEnum,
  ...RuleEditorConditionErrorsEnum,
  ...RuleEditorTriggerBaseErrorsEnum,
  ...ExpressionsEditorBaseErrorsEnum,
  ...RuleDataTableErrors,
};

export type RuleEditorErrorsEnum =
  | EditorBaseErrorsEnum
  | RuleEditorActionErrorsEnum
  | RuleEditorConditionErrorsEnum
  | RuleEditorTriggerBaseErrorsEnum
  | ExpressionsEditorBaseErrorsEnum
  | RuleDataTableErrors;

type RuleCheckerReturnType = ReadonlyArray<RuleEditorErrorsEnum>;

type ValidChecks = ReadonlyArray<keyof ReturnType<typeof makeRuleEditorErrorsChecks>>;

/*
 * Helpers.
 */

function makeRuleEditorErrorsChecks(
  state: RuleEditorState,
  ruleIntegration: RuleIntegration,
  hasHighlightErrorsFlag: boolean,
) {
  const {ruleState, expressions, errors, dataTable} = state;

  return {
    name(): RuleCheckerReturnType {
      if (!ruleState.name) {
        return [EditorBaseErrorsEnum.MISSING_NAME];
      }

      return [];
    },

    triggers(): RuleCheckerReturnType {
      if (ruleState.triggers.length === 0) {
        return [RuleEditorTriggerBaseErrorsEnum.NO_TRIGGER];
      }

      if (
        !hasHighlightErrorsFlag &&
        !ruleState.triggers.every((trigger) =>
          checkANDConditionsValidity(trigger.conditions, ruleIntegration, expressions),
        )
      ) {
        return [RuleEditorTriggerBaseErrorsEnum.INVALID_TRIGGER];
      }

      return [];
    },

    conditions(): RuleCheckerReturnType {
      const hasConditions =
        ruleState.conditions.length > 0 ||
        ruleState.triggers.some(({conditions}) => conditions.some((conds) => conds.length > 0));

      if (!hasConditions) {
        return [RuleEditorConditionErrorsEnum.NO_CONDITIONS];
      }

      if (
        !hasHighlightErrorsFlag &&
        !checkANDConditionsValidity(ruleState.conditions, ruleIntegration, expressions)
      ) {
        return [RuleEditorConditionErrorsEnum.INVALID_CONDITION];
      }

      return [];
    },

    actions(): RuleCheckerReturnType {
      if (ruleState.actions.length === 0) {
        return [RuleEditorActionErrorsEnum.NO_ACTION];
      }

      if (
        !hasHighlightErrorsFlag &&
        !ruleState.actions.every((action) =>
          checkActionValidity(action, ruleIntegration.findActionDefinition(action), expressions),
        )
      ) {
        return [RuleEditorActionErrorsEnum.INVALID_ACTION];
      }

      return [];
    },

    expressions(): RuleCheckerReturnType {
      const allAtomsAreValid = expressions.every((expression) =>
        expression.atoms.every((atom) => atom.isValid),
      );

      if (!allAtomsAreValid) {
        return [ExpressionsEditorBaseErrorsEnum.INVALID_ATOM];
      }

      return [];
    },

    dataTable(): RuleCheckerReturnType {
      if (!dataTable) {
        return [];
      }

      const tableErrors: Array<RuleDataTableErrors> = [];

      const columnIds = dataTable.columns.map((c) => c.id);
      if (columnIds.length > RULE_DATA_TABLE_MAX_COLUMN_COUNT) {
        tableErrors.push(RuleDataTableErrors.TOO_MANY_COLUMN);
      }

      if (columnIds.length > RULE_DATA_TABLE_MAX_ROW_COUNT) {
        tableErrors.push(RuleDataTableErrors.TOO_MANY_ROWS);
      }

      if (dataTable.rows.length === 0) {
        tableErrors.push(RuleDataTableErrors.NO_ROWS);
      }

      if (dataTable.rows.some((row) => columnIds.some((id) => !row[id]))) {
        tableErrors.push(RuleDataTableErrors.EMPTY_CELLS);
      }

      if (!expressions.some(expressionUsesDataTable)) {
        tableErrors.push(RuleDataTableErrors.UNUSED);
      }

      return tableErrors;
    },

    errors(): RuleCheckerReturnType {
      return hasHighlightErrorsFlag || isEmpty(errors) ? [] : [EditorValidationErrorsEnum.VALIDATION_ERRORS];
    },
  };
}

/**
 * This function gets errors for a list of checks.
 *
 * Example:
 * getErrorsForChecks(state, ruleIntegration, ['name', 'conditions'])
 * This check the name and conditions of the rule state.
 */
export function getRuleEditorErrorsForChecks(
  state: RuleEditorState,
  ruleIntegration: RuleIntegration,
  checks: ValidChecks,
  hasHighlightErrorsFlag = false,
): ReadonlyArray<RuleEditorErrorsEnum> {
  const errorsChecks = makeRuleEditorErrorsChecks(state, ruleIntegration, hasHighlightErrorsFlag);

  return flatten(checks.map((check) => errorsChecks[check]()));
}

function expressionUsesDataTable(expression: Expression) {
  return expression.atoms.some((atom) => atom.transform === TransformerIdsEnum.GET_COLUMN_VALUE);
}
